/** @module csi/widget/DateTimeTextBox */
define([
	"dojo/aspect",
	"dojo/_base/declare",
	"dijit/form/_FormValueWidget",
	"dijit/_WidgetsInTemplateMixin",
	"dojo/text!./DateTimeTextBox.html",
	"./_ErrorMessageMixin",
	"./DateTextBox",
	"./TimeTextBox"
], function(aspect,declare,_FormValueWidget,_WidgetsInTemplateMixin,template,_ErrorMessageMixin) {

	return declare([_FormValueWidget, _WidgetsInTemplateMixin, _ErrorMessageMixin], {
		// html template for plugin
		baseClass: "csiDateTimeBox",
		templateString: template,

		// Object: date and time constraints
		constraints: null,
		timeConstraints: null,

		// connection points
		focusNode: null,
		_dateNode: null,
		_timeNode: null,
		focusDefault: true,

		buildRendering: function(){
			// summary: apply date and time constraints
			this.inherited(arguments);
			this.focusNode = this._dateNode.focusNode;
			this._dateNode.isDisabledValue = this.isDisabledValue;
			this.params.dateParams ? this._dateNode.set(this.params.dateParams) : this._dateNode.set("constraints", this.constraints);
			this.params.timeParams ? this._timeNode.set(this.params.timeParams) : this._timeNode.set("constraints", this.timeConstraints);
		},

		focus: function() {
			// summary: focus on date node
			if (this.focusDefault && !this._timeNode.focused) {
				this._dateNode.focus();
			}
		},

		isDisabledValue: function(/*Date*/dt) {
			// summary: override to set logic for disabling values
			return false;
		},

		onClicked: function() {
			// summary: never called, but provided for consistency with DateTextBox and TimeTextBox (see csi/heading/TimeController)
		},

		onTextBoxInput: function(/*Event*/e) {
			// summary: expose onTextBoxInput from TimeTextBox
		},

		postCreate: function() {
			//summary: listen for the composed widgets to forward the error message
			var self = this;

			aspect.after(this._dateNode, "onErrorMessage", function(message) {
				self.onErrorMessage(self._getMessageAttr());
			}, true);

			aspect.after(this._timeNode, "onErrorMessage", function(message) {
				self.onErrorMessage(self._getMessageAttr());
			}, true);

			this.inherited(arguments);
		},

		toggleDropDown: function() {
			// summary: forward call to Date widget
			this._dateNode.toggleDropDown();
		},

		validate: function(/*Boolean*/isFocused) {
			// summary: validate both widgets
			this._dateNode._hasBeenBlurred = this._hasBeenBlurred;
			this._timeNode._hasBeenBlurred = this._hasBeenBlurred;
			var b1 = this._dateNode.validate(isFocused);
			var b2 = this._timeNode.validate(isFocused);
			return b1 && b2;
		},

		isValid: function(/*Boolean*/ isFocused) {
			// summary: apply both widgets
			var b1 = this._dateNode.isValid(isFocused);
			var b2 = this._timeNode.isValid(isFocused);
			return b1 && b2;
		},

		_getDateTimeValue: function() {
			// summary: combine date and time values into single date value
			var dt = this._dateNode.get("value");
			if (dt) {
				var time = this._timeNode.get("value");
				this._setConstraints(dt);
				if (time) {
					dt.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
				} else {
					dt.setHours(0, 0, 0, 0);
				}
			}
			return dt;
		},

		_getMessageAttr: function() {
			// summary: return message from both widget
			var dm = this._dateNode.get("message") || "";
			var tm = this._timeNode.get("message") || "";
			return dm + " "+ tm;
		},

		_getValueAttr: function() {
			// summary: return up to date value from data and time widgets rather than just returning existing value;
			// the value is delayed getting set on this widget since there is a timeout in widget _handleOnChange, so need to get directly from the source widgets
			return this._getDateTimeValue();
		},

		_onChangeValue: function() {
			// summary: extract value from data and time widgets and register change
			this._handleOnChange(this._getDateTimeValue());
		},

		/**
		* set time constraints if same day is selected
		* @param {DateObject} date - selected date 
		*/
		_setConstraints: function(date) {
			var currentDate = new Date();
			var constraintTime = this._timeNode.constraints;
			
			if (this._dateNode.constraints.inPresent && constraintTime.inPresent && currentDate.toDateString() === date.toDateString()) {
				constraintTime.min = currentDate;
			} else {
				delete constraintTime.min;
			}
		},

		_setCustomErrorHandlingAttr: function(/*Boolean*/b) {
			// summary: set customErrorHandling for both date and time
			this._dateNode.set("customErrorHandling", b);
			this._timeNode.set("customErrorHandling", b);
			this.inherited(arguments);
		},

		_setDisabledAttr: function(/*Boolean*/b) {
			// summary: set disabled for both date and time
			this._dateNode.set("disabled", b);
			this._timeNode.set("disabled", b);
			this.inherited(arguments);
		},

		_setDisabledDateAttr: function(/*Boolean*/b) {
			// summary: disable date widget
			this._dateNode.set("disabled", b);
			this.inherited(arguments);
		},

		_setDisabledTimeAttr: function(/*Boolean*/b) {
			// summary: disable time widget
			this._timeNode.set("disabled", b);
			this.inherited(arguments);
		},

		_setRequiredAttr: function(/*Boolean*/b) {
			// summary: set required for both date and time
			this._dateNode.set("required", b);
			this._timeNode.set("required", b);
			this.inherited(arguments);
		},

		_setValueAttr: function(/*Date|Number*/value) {
			// summary: set values for date and time widgets
			var dtOld = this._getDateTimeValue();
			var timeOld = dtOld ? dtOld.getTime() : 0;
			
			this._setValueDateAttr(value);
			this._setValueTimeAttr(value);
			
			var dtNew = this._getDateTimeValue();
			var timeNew = dtNew ? dtNew.getTime() : 0;

			if (timeOld !== timeNew) {
				this._handleOnChange(dtNew);
			}
		},

		/**
		 * Set value for date widget without triggering onChange function (pass false as third parameter)
		 * @param {Date|number} value
		 */
		_setValueDateAttr: function(value) {
			this._dateNode.set("value", value, false);
		},

		/**
		 * Set value for time widget without triggering onChange function (pass false as third parameter)
		 * @param {Date|number} value
		 */
		_setValueTimeAttr: function(value) {
			var dt = this._timeNode.normalizeValue(value);
			if (dt) {
				dt = new Date(0, 0, 0, dt.getHours(), dt.getMinutes(), dt.getSeconds(), dt.getMilliseconds());
			} else {
				// init to 12 noon
				dt = new Date(0, 0, 0, 12, 0, 0, 0);
			}
			
			this._timeNode.set("value", dt, false);
		}
	});
});